import React, { useEffect, useState } from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { ledgerAPI } from '../../services/api';

// Shared readable lookups for every ledger foreign key.
//
// Design rule (recognition over recall, error prevention): no input in the
// frontend ever asks for a raw database id. Each picker shows human-readable
// labels ("CODE - Name") and resolves the selection to the numeric id the
// API needs. The id itself is never displayed and never typed.
//
// Lists are cached module-wide for 5 minutes so dozens of pickers (e.g. one
// per journal line) share a single fetch.

const cache = {};
const inflight = {};
const TTL_MS = 5 * 60 * 1000;

const unwrapList = (response) => {
  const payload = response?.data?.data ?? response?.data ?? {};
  return Array.isArray(payload) ? payload : payload.items ?? [];
};

function loadCached(key, loader) {
  const hit = cache[key];
  if (hit && Date.now() - hit.at < TTL_MS) return Promise.resolve(hit.options);
  if (inflight[key]) return inflight[key];
  inflight[key] = loader()
    .then((options) => {
      cache[key] = { at: Date.now(), options };
      delete inflight[key];
      return options;
    })
    .catch((error) => {
      delete inflight[key];
      throw error;
    });
  return inflight[key];
}

export function useLedgerOptions(cacheKey, loader, enabled = true) {
  const [options, setOptions] = useState(() => cache[cacheKey]?.options ?? []);
  const [loading, setLoading] = useState(false);
  // loader is intentionally not a dep (inline arrow): the module cache keyed
  // by cacheKey is the source of truth, so one fetch serves all mounts.
  const loaderRef = React.useRef(loader);
  loaderRef.current = loader;

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    setLoading(true);
    loadCached(cacheKey, () => loaderRef.current())
      .then((opts) => {
        if (alive) {
          setOptions(opts);
          setLoading(false);
        }
      })
      .catch(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [cacheKey, enabled]);

  return { options, loading };
}

// Base lookup: value is always the numeric id (or '' when cleared).
// `exclude` hides ids that are not legal choices (e.g. the record itself).
export const LedgerPicker = ({
  label,
  value,
  onChange,
  options = [],
  loading = false,
  required = false,
  disabled = false,
  helper = undefined,
  placeholder = 'Type to search...',
  exclude = [],
  ...rest
}) => {
  const hidden = new Set((Array.isArray(exclude) ? exclude : [exclude]).filter((v) => v !== '' && v !== null && v !== undefined).map(String));
  const visible = hidden.size ? options.filter((o) => !hidden.has(String(o.value))) : options;
  const selected = value === '' || value === null || value === undefined
    ? null
    : visible.find((o) => String(o.value) === String(value)) ?? null;

  return (
    <Autocomplete
      fullWidth
      size="small"
      options={visible}
      loading={loading}
      getOptionLabel={(o) => o?.label ?? ''}
      value={selected}
      onChange={(_, o) => onChange(o ? o.value : '')}
      isOptionEqualToValue={(o, v) => String(o.value) === String(v.value)}
      renderInput={(params) => (
        <TextField {...params} label={label} required={required} helperText={helper} placeholder={placeholder} />
      )}
      disabled={disabled}
      {...rest}
    />
  );
};

const opt = (value, label) => ({ value, label });

const mapAccounts = (list) =>
  list.map((a) => opt(a.account_id ?? a.id, `${a.account_code ?? ''} - ${a.account_name ?? ''}`.trim()));

const mapClasses = (list) =>
  list.map((c) => opt(c.account_class_id ?? c.id, `${c.class_name ?? ''} (${c.code_start ?? ''}-${c.code_end ?? ''})`));

const mapTypes = (list) =>
  list.map((t) => opt(t.account_type_id ?? t.id, `${t.code ? `${t.code} - ` : ''}${t.type_name ?? ''}`));

const mapSubTypes = (list) =>
  list.map((s) => opt(s.sub_type_id ?? s.id, `${s.code ? `${s.code} - ` : ''}${s.sub_type_name ?? ''}`));

// typeId / activeOnly filter the raw list client-side before mapping to
// options — used by reparent (same account type, active parents only) so
// mismatches that the ledger would 400 on never show up as choices.
export const AccountPicker = ({ exclude, typeId, activeOnly, ...props }) => {
  const { options: raw, loading } = useLedgerOptions('accounts-raw', () =>
    ledgerAPI.getAccounts({ limit: 100 }).then((res) => unwrapList(res))
  );
  const filtered = raw.filter((a) => {
    if (typeId != null && String(a.sub_type?.accountType?.account_type_id ?? a.account_type_id ?? '') !== String(typeId)) return false;
    if (activeOnly && a.status !== 'ACTIVE') return false;
    return true;
  });
  return <LedgerPicker label="Account" exclude={exclude} {...props} options={mapAccounts(filtered)} loading={loading} />;
};

export const SalesInvoicePicker = (props) => {
  const { options, loading } = useLedgerOptions('ar-invoices', () =>
    ledgerAPI.getArInvoices({ limit: 100 }).then((res) =>
      unwrapList(res).map((i) => opt(i.invoice_id ?? i.id, i.invoice_number || `Invoice ${i.invoice_id ?? i.id}`))
    )
  );
  return <LedgerPicker label="Sales invoice" {...props} options={options} loading={loading} />;
};

export const PurchaseInvoicePicker = (props) => {
  const { options, loading } = useLedgerOptions('ap-invoices', () =>
    ledgerAPI.getApInvoices({ limit: 100 }).then((res) =>
      unwrapList(res).map((i) => opt(i.invoice_id ?? i.id, i.invoice_number || `Invoice ${i.invoice_id ?? i.id}`))
    )
  );
  return <LedgerPicker label="Purchase invoice" {...props} options={options} loading={loading} />;
};

export const AccountClassPicker = (props) => {
  const { options, loading } = useLedgerOptions('account-classes', () =>
    ledgerAPI.getAccountClasses({ limit: 100 }).then((res) => mapClasses(unwrapList(res)))
  );
  return <LedgerPicker label="Account class" {...props} options={options} loading={loading} />;
};

export const AccountTypePicker = (props) => {
  const { options, loading } = useLedgerOptions('account-types', () =>
    ledgerAPI.getAccountTypes({ limit: 100 }).then((res) => mapTypes(unwrapList(res)))
  );
  return <LedgerPicker label="Account type" {...props} options={options} loading={loading} />;
};

// typeId narrows the list to sub-types under one account type — used when
// create lets you pick a specific sub-type instead of leaving the ledger to
// arbitrarily resolve one via its own account_type_id shortcut.
export const SubTypePicker = ({ typeId, ...props }) => {
  const { options: raw, loading } = useLedgerOptions('account-sub-types-raw', () =>
    ledgerAPI.getAccountSubTypes({ limit: 100 }).then((res) => unwrapList(res))
  );
  const filtered = typeId != null
    ? raw.filter((s) => String(s.account_type_id ?? s.accountType?.account_type_id ?? '') === String(typeId))
    : raw;
  return <LedgerPicker label="Account sub-type" {...props} options={mapSubTypes(filtered)} loading={loading} />;
};

export const CurrencyPicker = (props) => {
  const { options, loading } = useLedgerOptions('currencies', () =>
    ledgerAPI.getCurrencies({ limit: 100 }).then((res) =>
      unwrapList(res).map((c) => opt(c.currency_id ?? c.id, `${c.currency_code ?? ''} - ${c.currency_name ?? ''}`.trim()))
    )
  );
  return <LedgerPicker label="Currency" {...props} options={options} loading={loading} />;
};

export const TaxCodePicker = (props) => {
  const { options, loading } = useLedgerOptions('tax-codes', () =>
    ledgerAPI.getTaxCodes({ limit: 100 }).then((res) =>
      unwrapList(res).map((t) => opt(t.tax_code_id ?? t.id, `${t.tax_code ?? ''} - ${t.tax_name ?? ''}`.trim()))
    )
  );
  return <LedgerPicker label="Tax code" {...props} options={options} loading={loading} />;
};

export const TaxTypePicker = (props) => {
  const { options, loading } = useLedgerOptions('tax-types', () =>
    ledgerAPI.getTaxTypes({ limit: 100 }).then((res) =>
      unwrapList(res).map((t) => opt(t.tax_type_id ?? t.id, `${t.tax_type_code ?? ''} - ${t.tax_type_name ?? ''}`.trim()))
    )
  );
  return <LedgerPicker label="Tax type" {...props} options={options} loading={loading} />;
};

export const BranchPicker = (props) => {
  const { options, loading } = useLedgerOptions('branches', () =>
    ledgerAPI.getBranches({ limit: 100 }).then((res) =>
      unwrapList(res).map((b) => opt(b.branch_id ?? b.id, `${b.branch_code ?? ''} - ${b.branch_name ?? ''}`.trim()))
    )
  );
  return <LedgerPicker label="Branch" {...props} options={options} loading={loading} />;
};

export const CustomerPicker = (props) => {
  const { options, loading } = useLedgerOptions('customers', () =>
    ledgerAPI.getCustomers({ limit: 100 }).then((res) =>
      unwrapList(res).map((c) => opt(c.customer_id ?? c.id, c.name || `Customer ${c.customer_id ?? c.id}`))
    )
  );
  return <LedgerPicker label="Customer" {...props} options={options} loading={loading} />;
};

export const SupplierPicker = (props) => {
  const { options, loading } = useLedgerOptions('suppliers', () =>
    ledgerAPI.getSuppliers({ limit: 100 }).then((res) =>
      unwrapList(res).map((s) => opt(s.supplier_id ?? s.id, s.name || `Supplier ${s.supplier_id ?? s.id}`))
    )
  );
  return <LedgerPicker label="Supplier" {...props} options={options} loading={loading} />;
};

export const ProductPicker = (props) => {
  const { options, loading } = useLedgerOptions('inventory-items', () =>
    ledgerAPI.getInventoryItems({ limit: 100 }).then((res) =>
      unwrapList(res).map((p) => opt(p.item_id ?? p.id, `${p.item_code ? `${p.item_code} - ` : ''}${p.item_name ?? ''}`))
    )
  );
  return <LedgerPicker label="Product" {...props} options={options} loading={loading} />;
};

export const DepartmentPicker = (props) => {
  const { options, loading } = useLedgerOptions('departments', () =>
    ledgerAPI.getDepartments({ limit: 100 }).then((res) =>
      unwrapList(res).map((d) => opt(d.department_id ?? d.id, `${d.department_code ?? ''} - ${d.department_name ?? ''}`.trim()))
    )
  );
  return <LedgerPicker label="Department" {...props} options={options} loading={loading} />;
};

export const LocationPicker = (props) => {
  const { options, loading } = useLedgerOptions('locations', () =>
    ledgerAPI.getLocations({ limit: 100 }).then((res) =>
      unwrapList(res).map((l) => opt(l.location_id ?? l.id, `${l.location_code ?? ''} - ${l.location_name ?? ''}`.trim()))
    )
  );
  return <LedgerPicker label="Location" {...props} options={options} loading={loading} />;
};

export const CostCentrePicker = (props) => {
  const { options, loading } = useLedgerOptions('cost-centres', () =>
    ledgerAPI.getCostCentres({ limit: 100 }).then((res) =>
      unwrapList(res).map((c) => opt(c.cost_centre_id ?? c.id, `${c.cost_centre_code ?? ''} - ${c.cost_centre_name ?? ''}`.trim()))
    )
  );
  return <LedgerPicker label="Cost centre" {...props} options={options} loading={loading} />;
};

export const DimensionPicker = (props) => {
  const { options, loading } = useLedgerOptions('dimensions', () =>
    ledgerAPI.getDimensions({ limit: 100 }).then((res) =>
      unwrapList(res).map((d) => opt(d.dimension_id ?? d.id, `${d.dimension_code ?? ''} - ${d.dimension_name ?? ''}`.trim()))
    )
  );
  return <LedgerPicker label="Dimension" {...props} options={options} loading={loading} />;
};

export const DimensionValuePicker = (props) => {
  const { options, loading } = useLedgerOptions('dimension-values', () =>
    ledgerAPI.getDimensionValues({ limit: 100 }).then((res) =>
      unwrapList(res).map((v) => opt(v.value_id ?? v.id, `${v.value_code ?? ''} - ${v.value_name ?? ''}`.trim()))
    )
  );
  return <LedgerPicker label="Dimension value" {...props} options={options} loading={loading} />;
};

export const FiscalYearPicker = (props) => {
  const { options, loading } = useLedgerOptions('fiscal-years', () =>
    ledgerAPI.getFiscalYears({ limit: 100 }).then((res) =>
      unwrapList(res).map((y) => opt(
        y.fiscal_year_id ?? y.id,
        `${y.year_name ?? ''}${y.is_closed ? ' (closed)' : ''}`
      ))
    )
  );
  return <LedgerPicker label="Fiscal year" {...props} options={options} loading={loading} />;
};

// Curated to reconciliation-linked GL accounts only (banking/accounts) -
// the right list for "which bank/cash account" fields (transfers, cheques,
// vouchers, petty cash), as opposed to AccountPicker's full chart of accounts.
export const BankAccountPicker = (props) => {
  const { options, loading } = useLedgerOptions('banking-accounts', () =>
    ledgerAPI.getBankingAccounts().then((res) =>
      unwrapList(res).map((a) => opt(a.account_id, `${a.account_code} - ${a.account_name}`))
    )
  );
  return <LedgerPicker label="Bank/cash account" {...props} options={options} loading={loading} />;
};

export const PaymentMethodPicker = (props) => {
  const { options, loading } = useLedgerOptions('payment-methods-raw', () =>
    ledgerAPI.getPaymentMethods({ limit: 100 }).then((res) =>
      unwrapList(res).map((m) => opt(m.method_id ?? m.id, m.method_name))
    )
  );
  return <LedgerPicker label="Payment method" {...props} options={options} loading={loading} />;
};

// methodId narrows to types under one payment method (used when the type
// picker follows a method choice in the same form).
export const PaymentMethodTypePicker = ({ methodId, ...props }) => {
  const key = `payment-method-types:${methodId || 'all'}`;
  const { options, loading } = useLedgerOptions(
    key,
    () => {
      const params = { limit: 100 };
      if (methodId) params.method_id = methodId;
      return ledgerAPI.getPaymentMethodTypes(params).then((res) =>
        unwrapList(res).map((t) => opt(t.method_type_id ?? t.id, t.display_name))
      );
    },
    true
  );
  return <LedgerPicker label="Payment method type" {...props} options={options} loading={loading} />;
};

export const FiscalPeriodPicker = ({ yearId, ...props }) => {
  const key = `fiscal-periods:${yearId || 'all'}`;
  const { options, loading } = useLedgerOptions(
    key,
    () => {
      const params = { limit: 100 };
      if (yearId) params.fiscal_year_id = yearId;
      return ledgerAPI.getFiscalPeriods(params).then((res) =>
        unwrapList(res).map((p) => opt(
          p.period_id ?? p.id,
          `${p.period_name ?? ''} (${(p.status || 'OPEN').toLowerCase()})`
        ))
      );
    },
    true
  );
  return <LedgerPicker label="Fiscal period" {...props} options={options} loading={loading} />;
};
